import type { Session } from "@supabase/supabase-js";
import type { Profile } from "../types/crm";
import { getAuthCallbackUrl } from "../lib/appUrl";
import { AuthError, mapAuthError } from "../lib/errors";

type AuthApi = {
  signInWithPassword(credentials: { email: string; password: string }): Promise<{
    data: { session: Session | null };
    error: unknown;
  }>;
  resetPasswordForEmail(email: string, options: { redirectTo: string }): Promise<{ error: unknown }>;
  updateUser(attributes: { password: string }): Promise<{ error: unknown }>;
};

export async function signInWithPassword(
  auth: AuthApi,
  email: string,
  password: string,
) {
  const { data, error } = await auth.signInWithPassword({ email, password });
  if (error) throw mapAuthError(error);
  if (!data.session) {
    throw new AuthError({
      code: "AUTH_REQUIRED",
      userMessage: "登录暂时无法完成，请稍后重试",
      technicalMessage: "Supabase signInWithPassword returned no session",
      retryable: true,
    });
  }
  return data.session;
}
export async function requestPasswordRecovery(auth: AuthApi, email: string) {
  const { error } = await auth.resetPasswordForEmail(email, {
    redirectTo: getAuthCallbackUrl("recovery"),
  });
  if (error) {
    const mapped = mapAuthError(error, "RECOVERY_REQUEST_FAILED");
    throw new AuthError({
      code: "RECOVERY_REQUEST_FAILED",
      userMessage: mapped.status === 429
        ? "请求过于频繁，请稍后再试"
        : "恢复邮件暂时无法发送，请稍后重试",
      technicalMessage: mapped.technicalMessage,
      status: mapped.status,
      retryable: true,
      cause: error,
    });
  }
}

export async function updatePassword(auth: AuthApi, password: string) {
  const { error } = await auth.updateUser({ password });
  if (error) throw mapAuthError(error, "PASSWORD_UPDATE_FAILED");
}

function profileErrorDetails(error: unknown) {
  return error as { code?: string; status?: number; message?: string } | null;
}

export function resolveProfileResult(
  result: { data: unknown[] | null; error: unknown },
): Profile {
  if (result.error) {
    const details = profileErrorDetails(result.error);
    const message = details?.message?.toLowerCase() ?? "";
    if (details?.code === "42501" || details?.status === 403 || message.includes("row-level security")) {
      throw new AuthError({
        code: "PROFILE_ACCESS_DENIED",
        userMessage: "当前账号无权读取个人资料，请联系管理员",
        technicalMessage: details?.message ?? "Profile query denied",
        status: 403,
        retryable: false,
        cause: result.error,
      });
    }
    if (
      result.error instanceof TypeError
      || message.includes("failed to fetch")
      || message.includes("network")
    ) {
      throw new AuthError({
        code: "PROFILE_NETWORK_ERROR",
        userMessage: "无法连接账号服务，请检查网络后重试",
        technicalMessage: details?.message ?? "Profile query network failure",
        retryable: true,
        cause: result.error,
      });
    }
    throw new AuthError({
      code: "PROFILE_LOAD_FAILED",
      userMessage: "个人资料暂时无法加载，请稍后重试",
      technicalMessage: details?.message ?? "Unknown profile query failure",
      status: details?.status,
      retryable: true,
      cause: result.error,
    });
  }

  const rows = (result.data ?? []) as Profile[];
  if (rows.length === 0) {
    throw new AuthError({
      code: "PROFILE_NOT_FOUND",
      userMessage: "账号资料尚未创建，请联系管理员",
      technicalMessage: "Profile query returned zero rows",
      retryable: false,
    });
  }
  if (rows.length > 1) {
    throw new AuthError({
      code: "PROFILE_DUPLICATE",
      userMessage: "账号资料存在异常，请联系管理员处理",
      technicalMessage: "Profile query returned " + rows.length + " rows",
      retryable: false,
    });
  }
  if (!rows[0].is_active) {
    throw new AuthError({
      code: "PROFILE_INACTIVE",
      userMessage: "账号已停用，请联系管理员",
      technicalMessage: "Profile is_active=false",
      status: 403,
      retryable: false,
    });
  }
  return rows[0];
}
